import { DateTime } from "luxon";
import {
  Badge,
  Button,
  Card,
  Colors,
  fontLarge,
  fontMedium,
  fontXSmall,
  formatDate,
  formatTime,
} from "swing-components";

/***** Constants *****/
// FIXME: Move this to `string-table.ts`
const START_TIME_LABEL = "Start time";
const END_TIME_LABEL = "End time";
const MEAL_BREAK_START_LABEL = (num: number, total: number) =>
  total > 1 ? `Meal break ${num} start` : "Meal break start";
const MEAL_BREAK_END_LABEL = (num: number, total: number) =>
  total > 1 ? `Meal break ${num} end` : "Meal break end";
const ENTER_TIME_LABEL = "Enter time";

/***** Types *****/
/**
 * Defines the status of a timesheet card which can only be one of the following:
 *
 * - `ACTION_REQUIRED`: When there is partial date for the work duration.
 * - `PAST_DUE`: When the date of the TimesheetCard is in the past.
 * - `SUBMITTED`: When the date of the TimesheetCard is in the future.
 */
export type TimesheetCardStatus = "ACTION_REQUIRED" | "PAST_DUE" | "SUBMITTED" | null;

type School = {
  id: string;
  name: string;
};

type Duration = {
  start?: DateTime;
  end?: DateTime;
};

type BreakPeriod = {
  start?: DateTime;
  end?: DateTime;
};

export type TimesheetCardProps = {
  requestDuration?: Duration;
  workDuration?: Duration;
  breakPeriods?: BreakPeriod[];
  date?: DateTime;
  location?: School;
  payCodeName?: string;
  totalHours?: string;
  status: TimesheetCardStatus;
  onEdit?: () => void;
};

/***** Component *****/
export function TimesheetCard(props: TimesheetCardProps) {
  const {
    requestDuration,
    workDuration,
    breakPeriods,
    date,
    location,
    payCodeName,
    totalHours,
    status,
    onEdit,
  } = props;

  const formattedDate = date ? formatDate({ date, format: "Wed, Jan 1" }) : undefined;

  const formattedStartTime = requestDuration?.start
    ? formatTime({ date: requestDuration.start, format: "1:00 pm" })
    : undefined;

  const formattedEndTime = requestDuration?.end
    ? formatTime({ date: requestDuration.end, format: "1:00 pm" })
    : undefined;

  const timeRange =
    formattedStartTime && formattedEndTime
      ? `${formattedStartTime} – ${formattedEndTime}`
      : undefined;

  return (
    <Card>
      {/* Status and Edit Icon */}
      <div
        css={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          {status && (
            <Badge
              // TODO: Move this all into a small utility
              status={status === "SUBMITTED" ? "success" : "warning"}
              text={
                status === "SUBMITTED"
                  ? "Submitted"
                  : status === "PAST_DUE"
                    ? "Past Due"
                    : "Action Required"
              }
            />
          )}
        </div>
        <div css={{ "& > button": { padding: "0 !important" } }}>
          <Button icon={{ name: "Edit" }} onClick={onEdit || (() => {})} type="primary" />
        </div>
      </div>

      {/* Main Content */}
      <div css={{ marginTop: 8 }}>
        <div css={{ marginBottom: 8 }}>
          <div css={{ ...fontLarge({ fontWeight: 500 }) }}>{formattedDate}</div>
          <div css={{ ...fontMedium() }}>{payCodeName || location?.name}</div>
          {timeRange && <div css={{ ...fontXSmall() }}>{timeRange}</div>}
        </div>

        <div css={{ marginTop: 8 }}>
          {payCodeName || totalHours ? (
            <Row label="Total Hours" value={totalHours || "--"} />
          ) : (
            <>
              <Row
                label={START_TIME_LABEL}
                value={
                  !workDuration?.start
                    ? ENTER_TIME_LABEL
                    : formatTime({ date: workDuration.start, format: "1:00 pm" })
                }
              />
              <Divider />
              <Row
                label={END_TIME_LABEL}
                value={
                  !workDuration?.end
                    ? ENTER_TIME_LABEL
                    : formatTime({ date: workDuration.end, format: "1:00 pm" })
                }
              />

              {breakPeriods && breakPeriods.length > 0 && (
                <>
                  <Divider thickness={2} color={Colors.slate300} />
                  {breakPeriods.map((breakPeriod, index) => (
                    <div key={index}>
                      <Row
                        label={MEAL_BREAK_START_LABEL(index + 1, breakPeriods.length)}
                        value={
                          !breakPeriod.start
                            ? ENTER_TIME_LABEL
                            : formatTime({ date: breakPeriod.start, format: "1:00 pm" })
                        }
                      />
                      <Divider />
                      <Row
                        label={MEAL_BREAK_END_LABEL(index + 1, breakPeriods.length)}
                        value={
                          !breakPeriod.end
                            ? ENTER_TIME_LABEL
                            : formatTime({ date: breakPeriod.end, format: "1:00 pm" })
                        }
                      />
                      {index < breakPeriods.length - 1 && (
                        <Divider thickness={2} color={Colors.slate300} />
                      )}
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

/***** Helper Components *****/
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      css={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
      }}
    >
      <span css={fontMedium({ fontWeight: 600 })}>{label}</span>
      <span css={fontMedium({ fontWeight: 400 })}>{value}</span>
    </div>
  );
}

function Divider(props: { thickness?: number; color?: string }) {
  return (
    <div
      css={{
        borderBottom: `${props.thickness || 1}px solid`,
        borderColor: props.color || Colors.slate200,
      }}
    />
  );
}
